import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthApiError } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { useBookableServices } from '@/hooks/usePublicBooking';
import { resolveClinicLogoUrl } from '@/lib/clinicBranding';
import { PortalIcon } from '@/components/portal/PortalIcon';
import {
  PortalAuthShell,
  PortalLangSwitch,
  applyPortalLoginLanguage,
  initialPortalLoginLanguage,
  portalLoginCopy,
  resolvePortalGuestBookHref,
} from '@/components/portal/PortalAuthShell';

export function PortalLogin() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [loginLang, setLoginLang] = useState<'da' | 'en'>(() =>
    initialPortalLoginLanguage(i18n.resolvedLanguage ?? i18n.language),
  );
  const copy = portalLoginCopy(loginLang);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const search = new URLSearchParams(location.search);
  const from =
    (location.state as { from?: Location } | null)?.from?.pathname ??
    search.get('from') ??
    '/portal/dashboard';
  const bookHref = resolvePortalGuestBookHref({
    search: location.search,
    fromPath: from,
  });
  const bookingSlug = bookHref?.replace(/^\/book\//, '') ?? null;
  const { data: bookingClinic } = useBookableServices(bookingSlug);
  const brandLogoUrl = resolveClinicLogoUrl(bookingClinic?.clinic_logo);
  const clinicName = bookingClinic?.clinic_name ?? 'Clinio';
  const setLang = (next: 'da' | 'en') => {
    applyPortalLoginLanguage({
      picked: next,
      persist: (lang) => {
        void i18n.changeLanguage(lang);
      },
    });
    setLoginLang(next);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Failed to verify session');
      const { error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('portal_user_id', authUser.id)
        .eq('status', 'active')
        .limit(1);
      if (patientError) throw new Error('Failed to verify patient record');
      navigate(from.startsWith('/portal') ? from : '/portal/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === 'invalid_credentials' || err.status === 400) {
          setError(loginLang === 'en' ? 'Invalid email or password' : 'Forkert e-mail eller adgangskode');
        } else if (err.status === 429) {
          setError(loginLang === 'en' ? 'Too many attempts. Try again later.' : 'For mange forsøg. Prøv igen senere.');
        } else {
          setError(loginLang === 'en' ? 'Something went wrong. Try again.' : 'Der opstod en fejl. Prøv igen.');
        }
      } else {
        setError(loginLang === 'en' ? 'Something went wrong. Try again.' : 'Der opstod en fejl. Prøv igen.');
      }
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async (event: FormEvent) => {
    event.preventDefault();
    if (resetStatus === 'loading') return;
    setResetStatus('loading');
    setError(null);
    try {
      await callFunction<{ email: string; audience: 'portal' }, { ok: true }>(
        'request-password-reset',
        { email: resetEmail, audience: 'portal' },
      );
      setResetStatus('success');
    } catch (err) {
      if (err instanceof ApiCallError && err.code === 'invalid-input') {
        setError(loginLang === 'en' ? 'Enter a valid email' : 'Skriv en gyldig e-mail');
      } else {
        setError(loginLang === 'en' ? 'Something went wrong. Try again.' : 'Der opstod en fejl. Prøv igen.');
      }
      setResetStatus('error');
    }
  };

  if (showResetForm && resetStatus === 'success') {
    return (
      <PortalAuthShell clinicName={clinicName} logoUrl={brandLogoUrl} lang={loginLang}>
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <div className="big-ic" style={{ background: 'var(--teal-tint)', color: 'var(--teal)' }}>
            <PortalIcon name="mail" />
          </div>
          <h1 style={{ fontSize: 22, lineHeight: '30px', fontWeight: 600, marginTop: 16 }}>{copy.sentTitle}</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            {copy.sentBody(resetEmail)}
          </p>
          <div className="stack" style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              className="btn btn-primary btn-block btn-lg"
              onClick={() => {
                setShowResetForm(false);
                setResetStatus('idle');
                setError(null);
              }}
            >
              {copy.back}
            </button>
            <button
              type="button"
              className="btn btn-tertiary btn-block"
              onClick={() => {
                setResetStatus('idle');
                void handlePasswordReset({ preventDefault() {} } as FormEvent);
              }}
            >
              {copy.resend}
            </button>
          </div>
        </div>
      </PortalAuthShell>
    );
  }

  if (showResetForm) {
    return (
      <PortalAuthShell
        clinicName={clinicName}
        logoUrl={brandLogoUrl}
        lang={loginLang}
        title={copy.forgotTitle}
        subtitle={copy.forgotSubtitle}
        under={
          <button
            type="button"
            className="link"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={() => {
              setShowResetForm(false);
              setResetStatus('idle');
              setError(null);
            }}
          >
            <PortalIcon name="chevronLeft" size="sm" />
            {copy.back}
          </button>
        }
      >
        <div className="card auth-card">
          {error ? <p className="help" style={{ color: 'var(--red-ink)' }}>{error}</p> : null}
          <form className="stack" onSubmit={(event) => void handlePasswordReset(event)}>
            <div className="field">
              <label htmlFor="em">{copy.email}</label>
              <input
                id="em"
                className="input"
                type="email"
                autoComplete="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={resetStatus === 'loading'}>
              {resetStatus === 'loading' ? copy.sending : copy.sendLink}
            </button>
          </form>
        </div>
      </PortalAuthShell>
    );
  }

  return (
    <PortalAuthShell
      clinicName={clinicName}
      logoUrl={brandLogoUrl}
      lang={loginLang}
      title={copy.title}
      subtitle={copy.subtitle}
      bookHref={bookHref}
      bookLabel={copy.book}
      under={
        <>
          <p>{copy.noAccount}</p>
          <PortalLangSwitch language={loginLang} onChange={setLang} />
        </>
      }
    >
      <div className="card auth-card">
        {error ? <p className="help" style={{ color: 'var(--red-ink)' }}>{error}</p> : null}
        <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
          <div className="field">
            <label htmlFor="em">{copy.email}</label>
            <input
              id="em"
              className="input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pw">{copy.password}</label>
            <div className="input with-ic">
              <input
                id="pw"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  border: 0,
                  height: '100%',
                  width: '100%',
                  background: 'transparent',
                  padding: 0,
                  letterSpacing: showPassword ? undefined : '0.2em',
                }}
              />
              <button
                className="eye"
                type="button"
                aria-label={showPassword ? copy.hidePassword : copy.showPassword}
                onClick={() => setShowPassword((value) => !value)}
              >
                <PortalIcon name="eye" />
              </button>
            </div>
          </div>
          <button type="button" className="link" style={{ alignSelf: 'flex-start', fontSize: 14 }} onClick={() => {
            setShowResetForm(true);
            setResetEmail(email);
            setError(null);
          }}>
            {copy.forgot}
          </button>
          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting}>
            {submitting ? copy.submitting : copy.submit}
          </button>
        </form>
      </div>
    </PortalAuthShell>
  );
}
